

import React from "react";
import Icon from "@mui/material/Icon";
import {
  builtinSidenavKeyToModuleCode,
  getModuleMaps,
  isEditionModuleAllowed,
  LEGACY_CRM_MODULES_HIDDEN_WHEN_TRAVEL,
  routeKeyToModuleCode,
} from "moduleEntitlementMap";

/** Built-in modules controllable in Sidebar Settings (route keys). */
/** Modules whose sidenav entry is a parent with children. */
const NESTED_PARENT_KEYS = new Set([
  "travel",
  "call-center",
  "real-estate",
  "re-sales",
  "re-brokerage",
  "re-leasing",
  "re-mortgages",
]);
/** Children of Travel: they show under Travel, not as their own settings rows. */
const TRAVEL_CHILD_KEYS_HIDDEN_FROM_SETTINGS = new Set(["travel-orders", "merchant"]);

/**
 * Sidenav items that are not sold as a module: the shell and the surfaces
 * that answer to more than one module. Module-backed items (leads, travel,
 * call-center, ...) come from the registry, so a module added there appears
 * here without an edit.
 */
const CORE_SIDENAV_META = {
  dashboard: { name: "Dashboard", icon: "dashboard", defaultSection: "sales" },
  // Cross-object, so it sits under Support rather than inside Call Center:
  // a tenant with case queues and no phone system still gets it.
  "work-queues": { name: "Work Queues", icon: "move_to_inbox", defaultSection: "support" },
  "agent-workload": { name: "Agent Workload", icon: "speed", defaultSection: "support" },
  inbox: { name: "Inbox", icon: "email", defaultSection: "productivity" },
  reports: { name: "Reports", icon: "assessment", defaultSection: "productivity" },
  dashboards: { name: "Dashboards", icon: "dashboard_customize", defaultSection: "productivity" },
  "custom-objects": { name: "Custom Objects", icon: "extension", defaultSection: "custom-objects" },
};

/** Core items plus the module items of a catalog (the snapshot by default). */
export function getBuiltinModuleMeta(catalog) {
  return { ...CORE_SIDENAV_META, ...getModuleMaps(catalog).sidenavMeta };
}

export const BUILTIN_MODULE_META = getBuiltinModuleMeta();


/** Dedicated sidebar section for tenant custom object modules. */
export const CUSTOM_OBJECTS_SECTION_ID = "custom-objects";

/**
 * Keys that require travel to be enabled. No longer what gates the sidenav
 * (each item's module and edition come from the registry); kept because
 * App.js un-hides these when Settings switches travel on.
 */
export const TRAVEL_GATED_KEYS = new Set([
  "travel",
  "website",
  "travel-orders",
  "merchant",
  "trips",
  "payment-templates",
  "payment-transactions",
  "high-season-surcharge",
  "trip-packages",
  "package-components",
  "departure-dates",
  "itineraries",
  "itinerary-days",
  "itinerary-day-line-items",
  "addons",
  "faqs",
]);

export function customObjectPrefId(apiName) {
  return `co_${apiName}`;
}

function normalizeCustomObjectItem(item) {
  if (!item?.id || typeof item.id !== "string" || !item.id.startsWith("co_")) {
    return item;
  }
  const apiName = item.apiName || item.api_name || item.id.replace(/^co_/, "");
  return {
    ...item,
    kind: "custom_object",
    apiName,
    icon: item.icon || "extension",
  };
}

/**
 * Custom objects used to be added hidden, so they never reached the sidenav
 * unless a user found the toggle. They are visible unless explicitly hidden.
 */
function applyCustomObjectVisibilityDefault(item) {
  if (!item?.hidden || item.visibilitySetByUser) return item;
  return { ...item, hidden: false };
}

function dedupeSections(sections) {
  const seen = new Set();
  return sections.filter((section) => {
    if (!section?.id || seen.has(section.id)) return false;
    seen.add(section.id);
    return true;
  });
}

/** Real Estate sidenav items, in the order they sit in the Sales section. */
const REAL_ESTATE_SIDENAV_IDS = [
  "real-estate",
  "re-sales",
  "re-brokerage",
  "re-leasing",
  "re-mortgages",
];

/** Default sidenav layout matching current Sales / Support / Productivity. */
export function getDefaultSidenavPreferences() {
  return {
    sections: [
      {
        id: "sales",
        title: "Sales",
        kind: "builtin",
        hidden: false,
        items: [
          { id: "dashboard", kind: "builtin", hidden: false },
          { id: "leads", kind: "builtin", hidden: false },
          { id: "accounts", kind: "builtin", hidden: false },
          { id: "contacts", kind: "builtin", hidden: false },
          { id: "deals", kind: "builtin", hidden: false },
          { id: "products", kind: "builtin", hidden: false },
          { id: "orders", kind: "builtin", hidden: false },
          { id: "travel", kind: "builtin", hidden: false },
          { id: "website", kind: "builtin", hidden: false },
          // Real Estate edition. Shown only to tenants holding the module.
          ...REAL_ESTATE_SIDENAV_IDS.map((id) => ({ id, kind: "builtin", hidden: false })),
          { id: "invoices", kind: "builtin", hidden: false },
        ],
      },
      {
        id: CUSTOM_OBJECTS_SECTION_ID,
        title: "Custom Objects",
        kind: "builtin",
        hidden: false,
        items: [],
      },
      {
        id: "support",
        title: "Support",
        kind: "builtin",
        hidden: false,
        items: [
          { id: "cases", kind: "builtin", hidden: false },
          { id: "work-queues", kind: "builtin", hidden: false },
          { id: "call-center", kind: "builtin", hidden: false },
          { id: "agent-workload", kind: "builtin", hidden: false },
        ],
      },
      {
        id: "productivity",
        title: "Productivity",
        kind: "builtin",
        hidden: false,
        items: [
          { id: "tasks", kind: "builtin", hidden: false },
          { id: "inbox", kind: "builtin", hidden: false },
          { id: "reports", kind: "builtin", hidden: false },
          { id: "dashboards", kind: "builtin", hidden: false },
        ],
      },
    ],
  };
}

/**
 * Merge tenant Custom Objects into sidenav preferences.
 * Existing object placement is preserved; newly discovered objects are added to
 * the dedicated Custom Objects section.
 * Returns { prefs, added } where added is true if preferences were reconciled.
 */
export function mergeCustomObjectsIntoPrefs(sidenavPrefs, customObjects = [], catalog = null) {
  // null means the object list could not be loaded: keep existing prefs untouched
  // instead of pruning every custom object off the sidenav.
  const knownObjects = Array.isArray(customObjects) ? customObjects : null;
  const base =
    sidenavPrefs?.sections?.length > 0
      ? {
          ...sidenavPrefs,
          sections: sidenavPrefs.sections.map((s) => ({
            ...s,
            items: [...(s.items || [])],
          })),
        }
      : getDefaultSidenavPreferences();

  base.sections = dedupeSections(base.sections);

  const objectsByApiName = new Map();
  (knownObjects || []).forEach((obj) => {
    const apiName = obj.apiName || obj.api_name;
    if (apiName) objectsByApiName.set(apiName, obj);
  });
  const activeApiNames = new Set(objectsByApiName.keys());

  let added = false;

  // Restore built-in Sales modules missing from older saved preferences
  // (e.g. prefs saved before Orders / Travel were added to the default layout).
  const salesSection = base.sections.find((section) => section.id === "sales");
  if (salesSection) {
    const salesItemIds = new Set((salesSection.items || []).map((item) => item.id));
    const insertBuiltin = (id, beforeIds = []) => {
      if (salesItemIds.has(id)) return;
      const item = { id, kind: "builtin", hidden: false };
      const beforeIndex = beforeIds
        .map((beforeId) => salesSection.items.findIndex((entry) => entry.id === beforeId))
        .find((index) => index >= 0);
      if (beforeIndex >= 0) {
        salesSection.items.splice(beforeIndex, 0, item);
      } else {
        salesSection.items.push(item);
      }
      salesItemIds.add(id);
      added = true;
    };

    // Orders sits before Travel when Travel exists; otherwise before Invoices.
    insertBuiltin("orders", ["travel", "travel-orders", "merchant", "invoices"]);
    insertBuiltin("travel", ["travel-orders", "merchant", "invoices"]);
    insertBuiltin("website", ["travel-orders", "merchant", "invoices"]);
    // Real Estate items go after the travel block, before Invoices, in order.
    REAL_ESTATE_SIDENAV_IDS.forEach((id) => insertBuiltin(id, ["invoices"]));
  }

  // Any module item the registry knows but this saved layout does not (an
  // add-on sold after the layout was saved, or a module added since) goes to
  // the end of its default section. Visibility is still decided by
  // isItemAllowed, so an item a tenant is not entitled to stays hidden.
  const placedIds = new Set(
    base.sections.flatMap((section) => (section.items || []).map((item) => item.id))
  );
  Object.entries(getModuleMaps(catalog).sidenavMeta).forEach(([id, meta]) => {
    if (placedIds.has(id)) return;
    if (TRAVEL_CHILD_KEYS_HIDDEN_FROM_SETTINGS.has(id)) return;
    const target =
      base.sections.find((section) => section.id === meta.defaultSection) ||
      base.sections.find((section) => section.kind === "builtin" && section.id !== CUSTOM_OBJECTS_SECTION_ID);
    if (!target) return;
    target.items = [...(target.items || []), { id, kind: "builtin", hidden: false }];
    placedIds.add(id);
    added = true;
  });

  // Ensure dedicated Custom Objects section exists
  let coSection = base.sections.find((s) => s.id === CUSTOM_OBJECTS_SECTION_ID);
  if (!coSection) {
    coSection = {
      id: CUSTOM_OBJECTS_SECTION_ID,
      title: "Custom Objects",
      kind: "builtin",
      hidden: false,
      items: [],
    };
    const supportIdx = base.sections.findIndex((s) => s.id === "support");
    if (supportIdx >= 0) {
      base.sections.splice(supportIdx, 0, coSection);
    } else {
      base.sections.push(coSection);
    }
    added = true;
  }

  // Reconcile custom objects in place so user-selected section placement survives reloads.
  const existingIds = new Set();
  base.sections = base.sections.map((section) => {
    const nextItems = [];
    (section.items || []).forEach((item) => {
      if (TRAVEL_CHILD_KEYS_HIDDEN_FROM_SETTINGS.has(item.id)) {
        added = true;   // marks prefs as changed so the Save button lights up
        return;
      }

      if (item.id === "custom-objects" && item.kind === "builtin") {
        added = true;
        return;
      }

      const isCustomObject =
        item.kind === "custom_object" || item.id?.startsWith?.("co_");
      if (!isCustomObject) {
        if (section.id === CUSTOM_OBJECTS_SECTION_ID) {
          added = true;
          return;
        }
        nextItems.push(item);
        return;
      }

      const normalized = applyCustomObjectVisibilityDefault(normalizeCustomObjectItem(item));
      const apiName =
        normalized.apiName || normalized.api_name || normalized.id?.replace(/^co_/, "");

      if (knownObjects && !activeApiNames.has(apiName)) {
        added = true;
        return;
      }
      if (existingIds.has(normalized.id)) {
        added = true;
        return;
      }

      const object = objectsByApiName.get(apiName);
      const reconciled = object
        ? {
            ...normalized,
            name:
              object.pluralName ||
              object.plural_name ||
              object.name ||
              apiName,
            apiName,
          }
        : normalized;
      if (
        reconciled.hidden !== item.hidden ||
        reconciled.kind !== item.kind ||
        reconciled.name !== item.name ||
        reconciled.apiName !== item.apiName
      ) {
        added = true;
      }
      existingIds.add(reconciled.id);
      nextItems.push(reconciled);
    });
    return { ...section, items: nextItems };
  });

  coSection = base.sections.find((s) => s.id === CUSTOM_OBJECTS_SECTION_ID);
  if (!coSection) return { prefs: base, added };

  if (coSection.hidden && !coSection.visibilitySetByUser) {
    coSection.hidden = false;
    added = true;
  }

  (knownObjects || []).forEach((obj) => {
    const apiName = obj.apiName || obj.api_name;
    if (!apiName) return;
    const id = customObjectPrefId(apiName);
    const displayName = obj.pluralName || obj.plural_name || obj.name || apiName;
    if (existingIds.has(id)) return;
    coSection.items.push(
      normalizeCustomObjectItem({
        id,
        kind: "custom_object",
        hidden: false,
        name: displayName,
        apiName,
        icon: "extension",
      })
    );
    existingIds.add(id);
    added = true;
  });

  return { prefs: base, added };
}

/**
 * Re-attach entitlement-filtered items so a settings save cannot permanently
 * drop modules the current user is not allowed to see right now.
 */
export function reconcileSidenavSectionsForSave(uiSections, previousSections = []) {
  const next = (uiSections || []).map((section) => ({
    ...section,
    items: [...(section.items || [])],
  }));
  const sectionById = new Map(next.map((section) => [section.id, section]));
  const uiItemIds = new Set();
  next.forEach((section) => {
    (section.items || []).forEach((item) => {
      if (item?.id) uiItemIds.add(item.id);
    });
  });

  (previousSections || []).forEach((prevSection) => {
    if (!prevSection?.id) return;
    let target = sectionById.get(prevSection.id);
    if (!target) {
      target = {
        ...prevSection,
        items: [],
      };
      next.push(target);
      sectionById.set(prevSection.id, target);
    }
    (prevSection.items || []).forEach((item) => {
      if (!item?.id || uiItemIds.has(item.id)) return;
      target.items.push(item);
      uiItemIds.add(item.id);
    });
  });

  return next;
}

function findRouteByKey(routes, key) {
  for (const route of routes) {
    if (route.key === key) return route;
    if (route.collapse) {
      const nested = findRouteByKey(route.collapse, key);
      if (nested) return nested;
    }
  }
  return null;
}

function isItemAllowed(itemId, options = {}) {
  // `role` is still in options and still threaded through by
  // buildSidenavRoutes, but nothing here reads it any more: the last rule
  // keyed on a role name was the Reports one below, and reports are now
  // governed by a permission grant. Left in the option shape rather than
  // ripped out of every caller, so a future rule that genuinely needs the
  // role has it to hand.
  const {
    isTravelEnabled = true,
    hasTravelEntitlement = true,
    enabledModuleCodes = null,
    enabledEditions = null,
    isCustomObjectsEnabled = false,
    entitlementsReady = true,
    moduleCatalog = null,
    isV2 = false,
  } = options;

  // Reports used to be hidden here from every standard user by role name.
  // Reports is a registered permission object now, so the grant decides, and a
  // blanket rule keyed on the role name would override it — an admin who
  // grants a rep read on reports should see the item appear for them.

  // A sidenav item's module, or for a child of a nested group (trips under
  // Travel, re-units under Real Estate) the module of its route key.
  const moduleCode =
    builtinSidenavKeyToModuleCode(itemId, moduleCatalog) ||
    routeKeyToModuleCode(itemId, moduleCatalog);

  // Travel ON: hide legacy Products / Quotations / Orders.
  // While entitlements are still loading, App.js may pass isTravelEnabled=false
  // even for travel tenants (it ANDs the company flag with hasTravelEntitlement).
  // Hide legacy during that window too so those items don't flash.
  // v2 keeps them: the Sales app owns them and the Travel app does not.
  if (
    !isV2 &&
    moduleCode &&
    LEGACY_CRM_MODULES_HIDDEN_WHEN_TRAVEL.has(String(moduleCode).toUpperCase())
  ) {
    if (isTravelEnabled || !entitlementsReady) {
      return false;
    }
  }

  // Avoid empty-Set deny-all while entitlements/feature licenses are still loading.
  if (!entitlementsReady) {
    return true;
  }

  if (itemId?.startsWith?.("co_") || itemId === "custom-objects") {
    return isCustomObjectsEnabled;
  }
  if (moduleCode) {
    // Before the module codes are known there is nothing to decide with.
    if (!enabledModuleCodes && moduleCode !== "TRAVEL") return true;
    return isEditionModuleAllowed(moduleCode, {
      enabledModuleCodes,
      enabledEditions,
      isTravelEnabled,
      hasTravelEntitlement,
      moduleCatalog,
      isV2,
    });
  }

  if (!hasTravelEntitlement || !isTravelEnabled) {
    if (TRAVEL_GATED_KEYS.has(itemId)) return false;
  }
  return true;
}

function iconFromName(iconName) {
  return <Icon fontSize="small">{iconName || "article"}</Icon>;
}

/**
 * Find a custom module by id inside sidenav preferences.
 */
export function findCustomModule(sidenavPrefs, moduleId) {
  if (!sidenavPrefs?.sections || !moduleId) return null;
  for (const section of sidenavPrefs.sections) {
    for (const item of section.items || []) {
      if (item.kind === "custom" && item.id === moduleId) {
        return { ...item, sectionId: section.id, sectionTitle: section.title };
      }
    }
  }
  return null;
}

/**
 * Update a custom module body (or fields) inside a prefs clone.
 */
export function updateCustomModuleInPrefs(sidenavPrefs, moduleId, patch) {
  const next = {
    ...sidenavPrefs,
    sections: (sidenavPrefs?.sections || []).map((section) => ({
      ...section,
      items: (section.items || []).map((item) =>
        item.id === moduleId && item.kind === "custom" ? { ...item, ...patch } : item
      ),
    })),
  };
  return next;
}

function isCustomObjectItem(item) {
  return item?.kind === "custom_object" || (typeof item?.id === "string" && item.id.startsWith("co_"));
}

/**
 * Build the route array expected by Sidenav from built-in routes + user prefs.
 * Does not alter React Router registration — display only.
 */
export function buildSidenavRoutes(allRoutes, sidenavPrefs, options = {}) {
  const {
    isTravelEnabled = true,
    hasTravelEntitlement = true,
    role = null,
    enabledModuleCodes = null,
    enabledEditions = null,
    isCustomObjectsEnabled = false,
    entitlementsReady = true,
    moduleCatalog = null,
    // Items marked v2Only (pages that exist only in the v2 look, such as the
    // Real Estate customer-portal Applications) are left out of the classic
    // sidenav.
    isV2 = false,
  } = options;
  const prefs =
    sidenavPrefs?.sections?.length > 0 ? sidenavPrefs : getDefaultSidenavPreferences();

  const itemOptions = {
    isTravelEnabled,
    hasTravelEntitlement,
    role,
    enabledModuleCodes,
    enabledEditions,
    isCustomObjectsEnabled,
    entitlementsReady,
    moduleCatalog,
    isV2,
  };

  const result = [];

  prefs.sections.forEach((section, sectionIndex) => {
    if (section.hidden) return;

    const visibleItems = [];
    const isCustomObjectsSection = section.id === CUSTOM_OBJECTS_SECTION_ID;

    if (isCustomObjectsSection && entitlementsReady && !isCustomObjectsEnabled) {
      return;
    }

    (section.items || []).forEach((item) => {
      if (item.hidden) return;

      if (isCustomObjectsSection) {
        if (!isCustomObjectItem(item)) return;
        const apiName = item.apiName || item.api_name || item.id.replace(/^co_/, "");
        if (!apiName) return;
        if (!isItemAllowed(item.id, itemOptions)) return;
        visibleItems.push({
          type: "collapse",
          name: item.name || apiName,
          key: item.id,
          icon: iconFromName(item.icon || "extension"),
          route: `/custom-objects/${apiName}`,
          hideInSidenav: false,
          isCustomObject: true,
        });
        return;
      }

      if (!isItemAllowed(item.id, itemOptions)) return;

      if (item.kind === "custom") {
        visibleItems.push({
          type: "collapse",
          name: item.name || "Custom Module",
          key: item.id,
          icon: iconFromName(item.icon),
          route: `/custom/${item.id}`,
          hideInSidenav: false,
          isCustomModule: true,
        });
        return;
      }

      if (isCustomObjectItem(item)) {
        const apiName = item.apiName || item.api_name || item.id.replace(/^co_/, "");
        if (!apiName) return;
        visibleItems.push({
          type: "collapse",
          name: item.name || apiName,
          key: item.id,
          icon: iconFromName(item.icon || "extension"),
          route: `/custom-objects/${apiName}`,
          hideInSidenav: false,
          isCustomObject: true,
        });
        return;
      }

      const builtin = findRouteByKey(allRoutes, item.id);
      if (!builtin) return;

      // Modules rendered as a parent with children (Travel, Call Center, the
      // Real Estate groups). Travel additionally carries a company-level
      // on/off flag; the rest only need entitlement.
      if (NESTED_PARENT_KEYS.has(item.id)) {
        if (item.id === "travel" && entitlementsReady && !isTravelEnabled) return;
        const nestedChildren = (builtin.collapse || []).filter(
          (child) =>
            !child.hideInSidenav &&
            (!child.v2Only || isV2) &&
            isItemAllowed(child.key, itemOptions)
        );
        visibleItems.push({
          ...builtin,
          hideInSidenav: false,
          collapse: nestedChildren.length
            ? nestedChildren.map((c) => ({ ...c, hideInSidenav: false }))
            : builtin.collapse,
        });
        return;
      }

      // Hub list page is settings-only; never show as a sidenav module
      if (item.id === "custom-objects") return;

      visibleItems.push({
        ...builtin,
        hideInSidenav: false,
      });
    });

    if (visibleItems.length === 0) return;

    if (sectionIndex > 0 && result.length > 0) {
      result.push({ type: "divider", key: `divider-${section.id}` });
    }

    result.push({
      type: "title",
      title: section.title,
      key: `section-${section.id}`,
    });

    visibleItems.forEach((route) => result.push(route));
  });

  return result;
}

/**
 * Common Material icon names for the settings picker.
 */
export const COMMON_SIDENAV_ICONS = [
  "article",
  "notes",
  "star",
  "bookmark",
  "folder",
  "dashboard",
  "work",
  "lightbulb",
  "checklist",
  "description",
  "link",
  "apps",
  "widgets",
  "extension",
  "topic",
];
